/**
 * Analytics for the RSVP funnel: page view -> form started -> submitted ->
 * seated/waitlisted -> email confirmed -> checked in (and cancellations).
 *
 * Everything here goes through `track()` in src/lib/analytics.ts, which strips
 * PII before anything leaves the browser. Never pass an email, name, phone,
 * callsign or pilot number into these helpers — only the event, the outcome
 * and coarse buckets.
 */

import { track, type AnalyticsProps } from "@/lib/analytics";
import { deviceType } from "@/lib/waitlist-telemetry";

/** How the visitor is RSVPing. */
export type RsvpEntry = "guest" | "signed_in" | "community" | "oauth" | "crew";

export type RsvpContextInput = {
  slug: string;
  eventType?: string | null;
  rsvpMode?: string | null;
  capacity?: number | null;
  spotsLeft?: number | null;
  requiresFreq?: boolean;
  introSession?: boolean;
  entry?: RsvpEntry;
};

/** Coarse fullness bucket — never the exact seat count. */
export function capacityState(
  spotsLeft: number | null | undefined,
  capacity: number | null | undefined,
): string {
  if (spotsLeft == null || capacity == null || capacity <= 0) return "unknown";
  if (spotsLeft <= 0) return "full";
  if (spotsLeft / capacity <= 0.25) return "almost_full";
  return "open";
}

/** Shared property bag attached to every RSVP funnel event. */
export function rsvpContext(input: RsvpContextInput): AnalyticsProps {
  const props: AnalyticsProps = {
    event_slug: input.slug,
    event_type: input.eventType ?? "unknown",
    rsvp_mode: input.rsvpMode ?? "internal",
    capacity_state: capacityState(input.spotsLeft, input.capacity),
    device: deviceType(),
  };
  if (input.requiresFreq !== undefined) props["requires_freq"] = input.requiresFreq;
  if (input.introSession !== undefined) props["intro_session"] = input.introSession;
  if (input.entry) props["entry"] = input.entry;
  return props;
}

const ERROR_PATTERNS: Array<[RegExp, string]> = [
  [/already (rsvp|on|have|signed)/i, "already_rsvped"],
  [/(full|no (seats|spots)|capacity)/i, "event_full"],
  [/(closed|not open|too late)/i, "rsvp_closed"],
  [/pass/i, "pass_rejected"],
  [/e-?mail/i, "invalid_email"],
  [/(sign in|signed in|unauthor|forbidden|permission)/i, "auth_required"],
  [/(expired|invalid|used)/i, "bad_link"],
  [/(network|fetch|timeout)/i, "network"],
];

/** Maps a thrown error to a short, non-identifying reason code. */
export function rsvpErrorCode(err: unknown): string {
  const message = err instanceof Error ? err.message : typeof err === "string" ? err : "";
  if (!message) return "unknown";
  for (const [pattern, code] of ERROR_PATTERNS) {
    if (pattern.test(message)) return code;
  }
  return "unknown";
}

/** Normalizes a server RSVP status into a funnel outcome. */
export function rsvpOutcome(status: string | null | undefined): string {
  switch (status) {
    case "waitlisted":
      return "waitlisted";
    case "going":
    case "attended":
      return "seated";
    case "cancelled":
      return "cancelled";
    default:
      return status ?? "unknown";
  }
}

export const RSVP_EVENTS = {
  pageViewed: "rsvp_page_viewed",
  formStarted: "rsvp_form_started",
  submitAttempted: "rsvp_submit_attempted",
  submitSucceeded: "rsvp_submit_succeeded",
  submitFailed: "rsvp_submit_failed",
  confirmViewed: "rsvp_confirm_viewed",
  confirmed: "rsvp_confirmed",
  confirmFailed: "rsvp_confirm_failed",
  checkedIn: "rsvp_checked_in",
  checkInUndone: "rsvp_check_in_undone",
  cancelled: "rsvp_cancelled",
  externalClicked: "rsvp_external_clicked",
  /** Pilot or crew changed an RSVP answer. */
  statusChanged: "rsvp_status_changed",
  /** Pilot clicked the pay-now button on the event page or in email. */
  payClicked: "rsvp_pay_clicked",
  /** Pilot came back from the payment page and said they paid. */
  payReturned: "rsvp_pay_returned",
  /** Crew (or a gateway) confirmed the money actually landed. */
  payConfirmed: "rsvp_pay_confirmed",
  /** Events list: someone tapped a card through to an event page. */
  listCardOpened: "events_list_card_opened",
  /** Events list: a filter, quick pick or search was used. */
  listFilterUsed: "events_list_filter_used",
  /** After-RSVP "tell us a bit more" card: a step answered or skipped. */
  followUpAnswered: "rsvp_follow_up_answered",
  followUpStopped: "rsvp_follow_up_stopped",
} as const;

export type RsvpEventName = (typeof RSVP_EVENTS)[keyof typeof RSVP_EVENTS];

/** Fires one RSVP funnel event. */
export function trackRsvp(event: RsvpEventName, props?: AnalyticsProps) {
  track(event, props);
}

/**
 * "Started the form" should only count once per visit to a form, so callers
 * hold a ref/flag and pass it here.
 */
export function makeFormStartTracker(props: () => AnalyticsProps) {
  let fired = false;
  return () => {
    if (fired) return;
    fired = true;
    trackRsvp(RSVP_EVENTS.formStarted, props());
  };
}
